<template>
  <div class="page-container task-page">
    <div class="card">
      <div class="card-header">
        <span class="card-title">任务管理</span>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">创建任务组</el-button>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-input
          v-model="queryForm.keyword"
          placeholder="任务组名称"
          clearable
          style="width: 200px"
          @keyup.enter.native="handleQuery"
        />
        <AreaCascader
          v-model="queryForm.zoneAreaId"
          :options="zoneAreaOptions"
          placeholder="执行大区"
          clearable
          filterable
          style="width: 160px"
          @change="handleQuery"
        />
        <el-select
          v-model="queryForm.taskType"
          placeholder="任务类型"
          clearable
          style="width: 130px"
          @change="handleQuery"
        >
          <el-option
            v-for="opt in taskTypeOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
        <el-select
          v-model="queryForm.cycleType"
          placeholder="周期类型"
          clearable
          style="width: 130px"
          @change="handleQuery"
        >
          <el-option
            v-for="opt in cycleTypeOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
        <el-select
          v-model="queryForm.taskYear"
          placeholder="年份（可输入搜索）"
          clearable
          filterable
          allow-create
          default-first-option
          style="width: 150px"
          @change="handleQuery"
        >
          <el-option
            v-for="y in yearOptions"
            :key="y"
            :label="`${y}年`"
            :value="y"
          />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">搜索</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 任务组卡片列表 -->
      <div v-loading="loading" class="task-group-list">
        <div v-if="tableData.length === 0" class="empty-placeholder">
          <i class="el-icon-folder-opened" />
          <p>暂无任务数据</p>
        </div>

        <div
          v-for="group in tableData"
          :key="group.uuid"
          class="task-group-card"
          @click="handleViewTasks(group)"
        >
          <!-- 左：标题区 -->
          <div class="card-left">
            <div class="group-name-wrapper">
              <span class="group-name">{{ group.groupName }}</span>
              <el-button
                icon="el-icon-edit"
                type="text"
                class="rename-btn"
                title="重命名任务组"
                @click.stop="handleRenameGroup(group)"
              />
            </div>
            <div class="group-tags">
              <el-tag :type="getTaskTypeTag(group.taskType)" size="small" effect="plain">
                {{ group.taskTypeName || group.taskType }}
              </el-tag>
              <el-tag :type="getCycleTypeTag(group.cycleType)" size="small" effect="plain">
                {{ group.cycleTypeName || group.cycleType }}
              </el-tag>
              <el-tag v-if="group.zoneAreaName" type="info" size="small" effect="plain">
                {{ group.zoneAreaName }}
              </el-tag>
              <el-tag v-if="group.taskYear" type="info" size="small" effect="plain">
                {{ group.taskYear }}年
              </el-tag>
            </div>
            <div class="group-meta">创建于 {{ formatDateTime(group.createTime) }}</div>
          </div>

          <!-- 中：统计区 -->
          <div class="card-stats">
            <div class="stat-item">
              <span class="stat-value">{{ group.totalSubTasks }}</span>
              <span class="stat-label">任务数</span>
            </div>
          </div>

          <!-- 右：操作区 -->
          <div class="card-actions" @click.stop>
            <el-button type="text" size="small" class="danger-text" @click="handleDeleteGroup(group)">删除任务组</el-button>
            <el-divider direction="vertical" class="action-divider"/>
            <span class="enter-text" @click.stop="handleViewTasks(group)">进入任务 <i class="el-icon-arrow-right" /></span>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        :current-page="pagination.pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 创建任务组弹窗 -->
    <TaskGroupDialog
      :visible.sync="createVisible"
      :area-tree="areaTree"
      :system-list="systemList"
      :type-list="typeList"
      @refresh="loadData"
    />
  </div>
</template>

<script>
// 服务
import { taskService } from './task.service';
import { areaService } from '@/features/gnmk/area/area.service';
import { deviceTypeService } from '@/features/gnmk/deviceType/deviceType.service';
// 工具函数
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { isTaskManager } from '@/features/common/utils/gnmkRoles';
import {
  TASK_TYPE_OPTIONS,
  CYCLE_TYPE_OPTIONS,
  getTaskTypeTag,
  getCycleTypeTag
} from '@/features/gnmk/constants/taskCycleEnums';
// 组件
import TaskGroupDialog from './task-group-dialog/index.vue';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'TaskList',
  components: { TaskGroupDialog, AreaCascader },
  data () {
    const currentYear = new Date().getFullYear();
    // 年份选项：前5年至后10年，共16个选项
    const startYear = currentYear - 5;
    const endYear = currentYear + 10;
    const yearOptions = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
    return {
      queryForm: {
        keyword: null,
        taskType: null,
        cycleType: null,
        zoneAreaId: null,
        taskYear: null
      },
      yearOptions,
      tableData: [],
      loading: false,
      pagination: { pageNo: 1, pageSize: 10, total: 0 },
      createVisible: false,
      areaTree: [],
      systemList: [],
      typeList: [],
      taskTypeOptions: TASK_TYPE_OPTIONS,
      cycleTypeOptions: CYCLE_TYPE_OPTIONS
    };
  },
  computed: {
    /** 与创建任务组一致：仅根级园区/场馆作为执行大区选项 */
    zoneAreaOptions () {
      return (this.areaTree || []).map(n => ({
        uuid: n.uuid,
        areaName: n.areaName
      }));
    }
  },
  methods: {
    loadData () {
      this.loading = true;
      taskService.queryTaskGroupPage({
        ...this.queryForm,
        pageNo: this.pagination.pageNo,
        pageSize: this.pagination.pageSize
      }).then(res => {
        const data = res.data;
        this.tableData = data.list || data.rows || [];
        this.pagination.total = data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },

    loadBaseData () {
      areaService.getAreaTree().then(res => (this.areaTree = res.data || []));
      deviceTypeService.listFireSystems().then(res => (this.systemList = res.data || []));
      deviceTypeService.listDeviceTypes().then(res => (this.typeList = res.data || []));
    },

    handleQuery () {
      this.pagination.pageNo = 1;
      this.loadData();
    },

    handleReset () {
      this.queryForm = {
        keyword: null,
        taskType: null,
        cycleType: null,
        zoneAreaId: null,
        taskYear: null
      };
      this.handleQuery();
    },

    handleSizeChange (val) {
      this.pagination.pageSize = val;
      this.pagination.pageNo = 1;
      this.loadData();
    },
    handleCurrentChange (val) {
      this.pagination.pageNo = val;
      this.loadData();
    },
    handleAdd () {
      this.createVisible = true;
    },

    /** 点击卡片跳转子任务列表页，将任务组关键信息全部传入 */
    handleViewTasks (group) {
      this.$router.push({
        name: 'taskTasks',
        query: {
          groupId: group.uuid,
          groupName: group.groupName,
          cycleType: group.cycleType,
          taskType: group.taskType,
          taskYear: group.taskYear || '',
          zoneAreaId: group.zoneAreaId || '',
          /** 与创建任务组一致：年度/年度日查是否按月分解（Y/N），子任务页用于期次下拉与筛选 */
          isSplitMonthly: group.isSplitMonthly != null && group.isSplitMonthly !== ''
            ? String(group.isSplitMonthly)
            : 'N'
        }
      });
    },

    handleRenameGroup (group) {
      this.$prompt('请输入新的任务组名称', '修改名称', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: group.groupName,
        inputPattern: /\S/,
        inputErrorMessage: '名称不能为空'
      }).then(({ value }) => {
        if (value === group.groupName) return;
        this.loading = true;
        taskService.updateTaskGroup({ uuid: group.uuid, groupName: value }).then(() => {
          this.$message.success('修改成功'); this.loadData();
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },

    handleDeleteGroup (group) {
      this.$confirm(`确认删除任务组"${group.groupName}"及其所有子任务吗？`, '警告', {
        type: 'warning',
        confirmButtonText: '确定删除',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        if (isTaskManager()) {
          return this.$confirm('当前账号为任务管理员，本次将强制删除该任务组下所有任务，包括执行中、已完成的任务。是否继续？', '强制删除确认', {
            type: 'warning',
            confirmButtonText: '强制删除',
            confirmButtonClass: 'el-button--danger'
          });
        }
        return null;
      }).then(() => {
        this.loading = true;
        taskService.deleteTaskGroup(group.uuid).then(() => {
          this.$message.success('删除成功');
          this.loadData();
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },

    getTaskTypeTag,
    getCycleTypeTag,
    formatDateTime
  },
  mounted () {
    this.loadBaseData();
    this.loadData();
  }
};
</script>

<style scoped lang="scss">
.task-page {
  padding: 15px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .card {
    flex: 1;
    min-height: 0; /* 与 taskList / device 页一致：父级定高时子项才能压缩并出现内部滚动 */
    background: #fff;
    border-radius: 4px;
    padding: 15px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    .card-header {
      flex-shrink: 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid #ebeef5;

      .card-title { font-size: 16px; font-weight: 600; color: #303133; }
    }

    .search-bar {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;
    }

    /* 任务组卡片区域占满剩余高度，多出时在区域内滚动，避免被框架内容区 overflow:hidden 裁切 */
    .task-group-list {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      margin-bottom: 10px;
    }

    .pagination { flex-shrink: 0; margin-top: 10px; text-align: right; }
  }

  .task-group-card {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    margin-bottom: 10px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
      border-color: #c6e2ff;
    }

    .card-left {
      flex: 1;
      min-width: 0;

      .group-name-wrapper {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .group-name {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .rename-btn {
          margin-left: 6px;
          padding: 0 4px;
          font-size: 14px;
          color: #409eff; /* 改为显眼的蓝色 */

          &:hover {
            color: #66b1ff;
          }
        }
      }

      .group-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 6px;
      }

      .group-meta { font-size: 12px; color: #c0c4cc; }
    }

    .card-stats {
      display: flex;
      gap: 30px;
      margin: 0 30px;

      .stat-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .stat-value { font-size: 20px; font-weight: bold; color: #303133; }
        .stat-label { font-size: 12px; color: #909399; }
      }
    }

    .card-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      white-space: nowrap;

      .action-divider {
        margin: 0 4px;
        height: 14px;
        background-color: #dcdfe6;
      }

      .enter-text {
        font-size: 14px;
        color: #409eff;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: color 0.2s;

        i {
          margin-left: 4px;
          transition: transform 0.2s;
        }

        &:hover {
          color: #66b1ff;
          i { transform: translateX(2px); }
        }
      }
    }
  }

  .empty-placeholder {
    padding: 60px 0;
    text-align: center;
    color: #909399;

    i { font-size: 48px; margin-bottom: 10px; color: #e4e7ed; display: block; }
    p { font-size: 14px; margin: 0; }
  }
}

.danger-text { color: #f56c6c; }
</style>
